import { useState, useEffect } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import { Spinner } from '@/components/ui/Spinner'
import { useAdmins } from '@/hooks/useAdmins'
import { useClientShares, useUpdateClientShares } from '@/hooks/useClientShares'
import type { Client } from '@/types/client'

interface ShareClientModalProps {
  client: Client
  isOpen: boolean
  onClose: () => void
}

interface ShareState {
  can_edit: boolean
}

export function ShareClientModal({ client, isOpen, onClose }: ShareClientModalProps) {
  const { data: admins, isLoading: loadingAdmins } = useAdmins()
  const { data: shares, isLoading: loadingShares } = useClientShares(client.id)
  const updateShares = useUpdateClientShares()

  const [selectedAdmins, setSelectedAdmins] = useState<Map<string, ShareState>>(new Map())

  // Get owner admin ID to exclude from share list
  const ownerAdminId = client.assigned_admins?.find((a) => a.is_owner)?.admin_id

  // Initialize selected admins from existing shares
  useEffect(() => {
    if (shares) {
      const initial = new Map<string, ShareState>()
      shares.forEach((share) => {
        initial.set(share.admin_id, { can_edit: share.can_edit })
      })
      setSelectedAdmins(initial)
    }
  }, [shares])

  const handleToggleAdmin = (adminId: string) => {
    const newMap = new Map(selectedAdmins)
    if (newMap.has(adminId)) {
      newMap.delete(adminId)
    } else {
      newMap.set(adminId, { can_edit: false })
    }
    setSelectedAdmins(newMap)
  }

  const handleToggleEdit = (adminId: string) => {
    const current = selectedAdmins.get(adminId)
    if (current) {
      const newMap = new Map(selectedAdmins)
      newMap.set(adminId, { can_edit: !current.can_edit })
      setSelectedAdmins(newMap)
    }
  }

  const handleSave = async () => {
    try {
      await updateShares.mutateAsync({
        clientId: client.id,
        shares: Array.from(selectedAdmins.entries()).map(([admin_id, { can_edit }]) => ({
          admin_id,
          can_edit,
          can_view: true,
        })),
      })
      onClose()
    } catch (error) {
      console.error('Failed to update shares:', error)
    }
  }

  const availableAdmins = admins?.filter((a) => a.id !== ownerAdminId) || []

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Partager le client" size="md">
      <div className="space-y-4">
        <p className="text-sm text-brown-600">
          Selectionnez les admins qui peuvent acceder a ce client
        </p>

        {loadingAdmins || loadingShares ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : availableAdmins.length === 0 ? (
          <p className="text-center py-4 text-brown-500">
            Aucun autre administrateur disponible
          </p>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {availableAdmins.map((admin) => (
              <div
                key={admin.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-beige-200"
              >
                <input
                  type="checkbox"
                  checked={selectedAdmins.has(admin.id)}
                  onChange={() => handleToggleAdmin(admin.id)}
                  className="w-4 h-4 text-mint-600 border-beige-300 rounded focus:ring-mint-500"
                />
                <Avatar src={admin.avatar_url} name={admin.full_name} size="sm" />
                <div className="flex-1">
                  <p className="font-medium text-brown-900">{admin.full_name}</p>
                  <p className="text-xs text-brown-500">{admin.email}</p>
                </div>
                {selectedAdmins.has(admin.id) && (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedAdmins.get(admin.id)?.can_edit || false}
                      onChange={() => handleToggleEdit(admin.id)}
                      className="w-4 h-4 text-mint-600 border-beige-300 rounded focus:ring-mint-500"
                    />
                    <span className="text-brown-600">Peut modifier</span>
                  </label>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3 pt-4 border-t border-beige-200">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1"
            isLoading={updateShares.isPending}
          >
            Enregistrer
          </Button>
        </div>
      </div>
    </Modal>
  )
}
